import { TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import ButtonBase from '@sorare/core/src/atoms/buttons/ButtonBase';
import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  LinkBox,
  LinkOther,
  LinkOverlay,
} from '@sorare/core/src/atoms/navigation/Box';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { rivals } from '@sorare/core/src/lib/glossary';

import { GameTeamsMatchup } from '@sorare/gameplay/src/components/game/Game/GameTeamsMatchup';

import { GameWrapper } from 'components/rivals/RivalsMatchesDateLayout/GameWrapper';
import { SetReminder } from 'components/rivals/SetReminder';

import { FeaturedGame_footballRivalsGame } from './__generated__/index.graphql';

const StyledRivalsGame = styled(GameWrapper)`
  height: 100%;
`;
const CompetitionNameWrapper = styled.div`
  border-radius: var(--double-unit);
  padding: var(--half-unit) var(--unit);
  background: rgba(var(--c-rgb-neutral-1000), 0.17);
`;
const HorizontalWithPadding = styled(SBHorizontal)`
  padding: var(--double-unit) var(--double-unit) var(--unit);
  min-height: var(--triple-unit);
`;

type Props = {
  footballRivalsGame: FeaturedGame_footballRivalsGame;
  onClick: (slug: string, lineupId?: string) => void;
  selected?: boolean;
  showSetReminder?: boolean;
};

export const FeaturedGame = ({
  footballRivalsGame,
  onClick,
  selected,
  showSetReminder = true,
}: Props) => {
  const { formatMessage } = useIntl();
  const { game, myLineup, slug } = footballRivalsGame;

  return (
    <LinkBox>
      <StyledRivalsGame
        className={classNames({ selected })}
        labelProps={
          myLineup
            ? {
                value: formatMessage(rivals.joined),
                bgColor: 'var(--c-rivals-friendlies)',
                color: 'var(--c-neutral-100)',
              }
            : undefined
        }
        lightningColor={game.homeTeam?.styleGuideColor}
      >
        <Vertical gap={0}>
          <HorizontalWithPadding gap={1}>
            <CompetitionNameWrapper>
              <LabelS as="span" bold>
                {game.competition.displayName}
              </LabelS>
            </CompetitionNameWrapper>
            {!myLineup && showSetReminder && (
              <LinkOther>
                <SetReminder footballRivalsGame={footballRivalsGame} />
              </LinkOther>
            )}
          </HorizontalWithPadding>
          <LinkOverlay
            as={ButtonBase}
            onClick={() => {
              onClick(slug, myLineup?.id);
            }}
          >
            <GameTeamsMatchup game={footballRivalsGame.game} />
          </LinkOverlay>
        </Vertical>
      </StyledRivalsGame>
    </LinkBox>
  );
};

FeaturedGame.fragments = {
  footballRivalsGame: gql`
    fragment FeaturedGame_footballRivalsGame on FootballRivalsGame {
      slug
      myLineup {
        id
      }
      game {
        id
        competition {
          slug
          displayName
        }
        homeTeam {
          slug
          styleGuideColor
        }
        ...GameTeamsMatchup_anyGameInterface
      }
      ...SetReminder_footballRivalsGame
    }
    ${GameTeamsMatchup.fragments.anyGameInterface}
    ${SetReminder.fragments.footballRivalsGame}
  ` as TypedDocumentNode<FeaturedGame_footballRivalsGame>,
};
